import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import bill components (lazy-loaded for performance)
const Bills = React.lazy(() => import("./Bills"));
const NewBill = React.lazy(() => import("./NewBill"));
const BillDetails = React.lazy(() => import("./BillDetails"));
const BillPDFPreview = React.lazy(() => import("./BillPDFPreview"));
const PaymentReceivingDetails = React.lazy(() => import("./PaymentReceivingDetails"));
const EditPaymentDetails = React.lazy(() => import("./EditPaymentDetails"));
const CompanyDetails = React.lazy(() => import("./CompanyDetails"));

// Subscription components (consolidated from subscriptions module)
// SubscriptionsDashboard removed — thin stats page that overlaps with SubscriptionBilling
const ManageSubscriptions = React.lazy(() => import("./ManageSubscriptions"));
const CreateSubscription = React.lazy(() => import("./CreateSubscription"));
const EditSubscription = React.lazy(() => import("./EditSubscription"));
const SubscriptionDetail = React.lazy(() => import("./SubscriptionDetail"));
const PaymentMethods = React.lazy(() => import("./PaymentMethods"));
const ManualCharging = React.lazy(() => import("./ManualCharging"));
// BillingSettings removed — non-functional mockup, all settings handled in EditPaymentDetails

// SubscriptionBilling removed — consolidated into ManageSubscriptions page

// Subscription product definitions (admin CRUD)
const SubscriptionProducts = React.lazy(() => import("./SubscriptionProducts"));
const Coupons = React.lazy(() => import("./Coupons"));

// Notification rules
const BillNotificationRules = React.lazy(() => import("./BillNotificationRules"));

// Email branding (per-module override of tenant.branding for bills emails)
const EmailBrandingSettings = React.lazy(() => import("./EmailBrandingSettings"));

// Reporting + the stored weekly snapshot
const BillsReporting = React.lazy(() => import("./BillsReporting"));
const BillsReportSettings = React.lazy(() => import("./BillsReportSettings"));

// Public pages
const SubscribePage = React.lazy(() => import("./SubscribePage"));
const BillPaymentSuccess = React.lazy(() => import("./BillPaymentSuccess"));

const billsModuleConfig: ModuleConfig = {
    // MODULE IDENTITY
    id: "bills",
    name: "Billing & Subscriptions",
    description: "Invoicing, subscription management, and payment processing",
    version: "2.0.1",
    enabled: true,
    category: "billing",
    order: 6,
    icon: "💳",
    color: "#E53E3E",

    // ROUTES
    routes: [
        // --- Invoicing ---
        {
            path: "/bills",
            component: Bills,
            permissionLevel: "view",
            useLayout: true
        },
        {
            path: "/bills/new",
            component: NewBill,
            permissionLevel: "create",
            useLayout: true
        },
        {
            path: "/bills/payment-details",
            component: PaymentReceivingDetails,
            permissionLevel: "edit",
            useLayout: true
        },
        {
            path: "/bills/payment-details/edit",
            component: EditPaymentDetails,
            permissionLevel: "edit",
            useLayout: true
        },
        {
            path: "/bills/reporting",
            component: BillsReporting,
            permissionLevel: "view",
            useLayout: true
        },
        {
            // Settings surface for the module's reporting: the weekly-snapshot
            // toggle, the backfill window, and the stored snapshots themselves.
            path: "/bills/reporting/settings",
            component: BillsReportSettings,
            permissionLevel: "edit",
            useLayout: true
        },
        {
            path: "/bills/company-details",
            component: CompanyDetails,
            permissionLevel: "edit",
            useLayout: true,
            layoutOptions: { maxW: "container.lg" }
        },
        {
            path: "/bill/:id",
            component: BillDetails,
            public: true,
            useLayout: true
        },
        {
            path: "/bill/:id/preview",
            component: BillPDFPreview,
            public: true,
            useLayout: true
        },
        {
            path: "/bill/:id/payment-success",
            component: BillPaymentSuccess,
            public: true,
            useLayout: true,
            layoutOptions: { noBreadcrumb: true, maxW: "container.lg" }
        },
        {
            path: "/subscribe/:slug",
            component: SubscribePage,
            public: true,
            useLayout: true,
            layoutOptions: { noBreadcrumb: true, maxW: "container.lg" }
        },
        // --- Subscriptions ---
        {
            path: "/bills/subscriptions/manage",
            component: ManageSubscriptions,
            permissions: ["ADMIN", "MANAGER"],
            useLayout: true
        },
        {
            path: "/bills/subscriptions/create",
            component: CreateSubscription,
            permissions: ["ADMIN"],
            useLayout: true,
            layoutOptions: { maxW: "container.md" }
        },
        {
            path: "/bills/subscriptions/:id/edit",
            component: EditSubscription,
            permissions: ["ADMIN"],
            useLayout: true
        },
        // Detail is the DEFAULT destination for a subscription; editing is a
        // deliberate second step. Before this route the kebab menu jumped
        // straight to /edit, so an edit form was serving as the detail view —
        // part of why a decorative nextBillingDate read as a live schedule.
        // Declared AFTER /:id/edit so the literal segment wins the match.
        {
            path: "/bills/subscriptions/:id",
            component: SubscriptionDetail,
            permissions: ["ADMIN"],
            useLayout: true
        },
        {
            path: "/bills/subscriptions/payment-methods",
            component: PaymentMethods,
            permissions: ["ADMIN"],
            useLayout: true,
            layoutOptions: { maxW: "container.lg" }
        },
        {
            path: "/bills/subscriptions/manual-charging",
            component: ManualCharging,
            permissions: ["ADMIN"],
            useLayout: true
        },
        // --- Subscription Products ---
        {
            path: "/bills/subscription-products",
            component: SubscriptionProducts,
            permissions: ["ADMIN"],
            useLayout: true
        },
        // --- Coupons (promo codes) ---
        {
            path: "/bills/coupons",
            component: Coupons,
            permissionLevel: "edit",
            useLayout: true
        },
        // --- Notification Rules ---
        {
            path: "/bills/notification-settings",
            component: BillNotificationRules,
            permissionLevel: "edit",
            useLayout: true,
            layoutOptions: { maxW: "container.lg" }
        },
        // --- Email Branding (per-module override of tenant.branding) ---
        {
            path: "/bills/email-branding",
            component: EmailBrandingSettings,
            permissionLevel: "edit",
            useLayout: true,
            layoutOptions: { maxW: "container.xl" }
        }
    ],

    // NAVIGATION
    navigation: [
        // Invoicing section
        {
            label: "All Bills",
            path: "/bills",
            icon: "📄",
            permissionLevel: "view"
        },
        {
            label: "New Bill",
            path: "/bills/new",
            icon: "➕",
            permissionLevel: "create"
        },
        {
            label: "Payment Details",
            path: "/bills/payment-details",
            icon: "💳",
            permissionLevel: "edit"
        },
        {
            label: "Reporting",
            path: "/bills/reporting",
            icon: "📊",
            permissionLevel: "view"
        },
        {
            label: "Report Settings",
            path: "/bills/reporting/settings",
            icon: "⚙️",
            permissionLevel: "edit"
        },
        {
            label: "Company Details",
            path: "/bills/company-details",
            icon: "🏢",
            permissionLevel: "edit"
        },
        // Subscriptions section
        {
            label: "Subscriptions",
            path: "/bills/subscriptions/manage",
            icon: "📋",
            permissions: ["ADMIN", "MANAGER"]
        },
        {
            label: "Coupons",
            path: "/bills/coupons",
            icon: "🏷️",
            permissionLevel: "edit"
        },
        {
            label: "Payment Methods",
            path: "/bills/subscriptions/payment-methods",
            icon: "💳",
            permissions: ["ADMIN"]
        },
        {
            label: "Manual Charging",
            path: "/bills/subscriptions/manual-charging",
            icon: "⚡",
            permissions: ["ADMIN"]
        },
        {
            label: "Sub Products",
            path: "/bills/subscription-products",
            icon: "🎯",
            permissions: ["ADMIN"]
        },
        {
            label: "Notifications",
            path: "/bills/notification-settings",
            icon: "🔔",
            permissionLevel: "edit"
        },
        {
            label: "Email Branding",
            path: "/bills/email-branding",
            icon: "🎨",
            permissionLevel: "edit"
        }
    ],

    // PERMISSIONS (resolved from backend single-source-of-truth)
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: []
    },

    // QUICK ACTIONS
    quickActions: [
        {
            label: "Create Bill",
            path: "/bills/new",
            icon: "➕",
            description: "Create a new invoice or bill"
        },
        {
            label: "New Subscription",
            path: "/bills/subscriptions/create",
            icon: "📋",
            description: "Create a new subscription"
        },
        {
            label: "Charge Client",
            path: "/bills/subscriptions/manual-charging",
            icon: "⚡",
            description: "Charge a client's saved payment method"
        }
    ],

    // CHANGELOG
    changelog: [
        {
            version: "2.0.0",
            releaseDate: "2026-02-27",
            title: "Billing & Subscriptions Consolidation",
            features: [
                "✅ Consolidated subscriptions module into billing",
                "✅ Consolidated subscription-billing dashboard into billing",
                "✅ Subscription product definitions (database-driven)",
                "✅ Removed Stripe env var fallbacks (tenant keys only)",
                "✅ Unified module breadcrumb navigation"
            ],
            notes: "Major consolidation of 3 separate billing/subscription modules into one unified Billing & Subscriptions module."
        },
        {
            version: "1.2.0",
            releaseDate: "2025-11-22",
            title: "Production Release",
            features: [
                "✅ Comprehensive changelog history documentation",
                "✨ Enhanced authentication module integration",
                "✨ Enhanced dark mode color handling across bill components"
            ],
            notes: "Production-ready release with complete feature history."
        }
    ],

    // ROADMAP
    roadmap: []

};

export default billsModuleConfig;
